/**
 * StageModelsWidget — "which models is this screen going to spend my money on?"
 *
 * Every screen that runs agents used to answer that with a single hard-coded
 * <AgentModelChip>, picked by whichever stage the author had in mind. A page
 * build actually bills seven stages; a room bills four. The other six were
 * invisible, and the one that showed drifted whenever a stage moved.
 *
 * So this is one collapsed control — a chip glyph with a count — that expands
 * into the full list: what each stage does HERE (its role + blurb), the model
 * resolved for it, and, crucially, whether that model was chosen for the stage
 * or merely inherited from the account DEFAULT. That last distinction is the
 * reason the widget exists: an inherited model silently changes under you when
 * you retune DEFAULT for something else, and nothing in the old chip said so.
 *
 *   <StageModelsWidget surface="page_builder" />   // backend owns the stage list
 *   <StageModelsWidget stages={['GENERATE_ARTICLE']} />  // ad-hoc
 *
 * Each row deep-links to that stage's row in pipeline settings (the same
 * destination AgentModelChip uses), so seeing a wrong model and fixing it is
 * one click, not a hunt through the settings page.
 *
 * THE PANEL IS PORTALLED, and that is not decoration. As an ordinary absolutely
 * positioned child it lost to two different ancestors: the brainstorm run bar is
 * `sticky z-30` with a backdrop-blur, which both matches the panel's z-index and
 * comes LATER in the document, so it painted on top; and the room's card is
 * `overflow-clip`, which crops an absolute descendant no matter what z-index it
 * carries. Raising the z-index fixes exactly one of those and only until the
 * next sticky header appears. Rendering into <body> at a fixed position escapes
 * every ancestor stacking context and every overflow container at once, for all
 * twelve mount points and whatever the thirteenth turns out to sit inside.
 */
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useAnchoredPanel from './useAnchoredPanel';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';
import { useShowInternals } from '../../contexts/InternalsContext';
import {
  agentSettingsUrl, seatSettingsUrl,
  pipelineSettingsUrl,
  useReturnToHere,
} from './AgentModelChip';
import type { StageModelInfo } from '../../types';

interface StageModelsWidgetProps {
  /** Surface key registered in the backend's stage_surfaces registry. */
  surface?: string;
  /** Explicit stage values, for callers not backed by a surface. */
  stages?: string[];
  /**
   * What to call this screen on the settings page's return button — every link
   * out of this panel carries the trail back here. Omit for a plain "← Back".
   */
  returnLabel?: string;
  /** Extra classes on the trigger button (positioning is the caller's job). */
  className?: string;
}

/** Matches the w-96 this panel has always been. */
const PANEL_WIDTH = 384;

/** Capabilities worth calling out on a row; TEXT is the unremarkable default. */
const CAPABILITY_BADGE: Record<string, string> = {
  tools: 'tools',
  vision: 'vision',
  image_generation: 'image',
};

export default function StageModelsWidget({
  surface,
  stages,
  returnLabel,
  className = '',
}: StageModelsWidgetProps) {
  const [models, setModels] = useState<StageModelInfo[]>([]);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  /** Viewport coordinates for the portalled panel; null until measured. */
  const pos = useAnchoredPanel(open, triggerRef, PANEL_WIDTH);
  const returnTo = useReturnToHere(returnLabel);
  // Which model runs a stage is internals: interesting to whoever tunes the
  // pipeline, noise to whoever bought the product. Gated here rather than at the
  // twelve call sites, so a thirteenth inherits it.
  const showInternals = useShowInternals();

  // Serialize the stage list so the effect re-runs on content change, not on
  // every render that hands us a fresh array literal.
  const stageKey = stages?.join(',') ?? '';

  useEffect(() => {
    let cancelled = false;
    // A hidden widget costs no request — this fires on nearly every screen.
    if (!showInternals) return;
    if (!surface && !stageKey) return;
    apiService
      .resolveStageModels({
        surface,
        stages: stageKey ? stageKey.split(',') : undefined,
      })
      .then(res => {
        if (!cancelled) {
          setModels(res);
          setError(false);
        }
      })
      // This panel is informational — a failed lookup hides it rather than
      // breaking the screen it's docked to.
      .catch(() => !cancelled && setError(true));
    return () => {
      cancelled = true;
    };
  }, [surface, stageKey, showInternals]);

  // Close on Escape as well as click-away; the panel is a menu, not a dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!showInternals || error || models.length === 0) return null;

  const missing = models.filter(m => !m.label).length;
  const inherited = models.filter(m => m.label && m.inherited).length;
  // The number on the chip counts STAGES, not distinct models — four stages
  // routinely resolve to one or two models (they inherit the same DEFAULT), so
  // read as a model count it just looks wrong. Both the label and the panel's
  // inherited note carry the denominator to say which it is.
  const chipLabel = `Models used by the ${models.length} stages on this screen`;

  return (
    <div className="relative" ref={boxRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-label={chipLabel}
        title={
          missing
            ? `${models.length} stages run on this screen - ${missing} with no model configured`
            : `${models.length} stages run on this screen`
        }
        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium transition-colors ${
          missing
            ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:border-amber-500'
            : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-indigo-300 dark:hover:border-indigo-700 hover:text-indigo-700 dark:hover:text-indigo-300'
        } ${className}`}
      >
        {/* Same node glyph AgentModelChip uses — one visual vocabulary for "model". */}
        <svg
          className="w-3 h-3 opacity-70"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
        <span className="tabular-nums">{models.length}</span>
        {missing > 0 && <span className="font-semibold">!</span>}
        <span className="text-gray-400 dark:text-gray-500">{open ? '▴' : '▾'}</span>
      </button>

      {open &&
        pos &&
        createPortal(
        <>
          {/* click-away layer */}
          <div className="fixed inset-0 z-[100]" onClick={() => setOpen(false)} />
          <div
            style={{
              top: pos.top,
              bottom: pos.bottom,
              left: pos.left,
              width: PANEL_WIDTH,
              maxHeight: pos.maxHeight,
            }}
            className="fixed z-[101] max-w-[calc(100vw-1rem)] overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-3 text-left shadow-lg">
            <div className="mb-2 flex items-baseline justify-between gap-2">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Models by stage
              </span>
              {inherited > 0 && (
                <span
                  className="text-[10px] text-gray-400 dark:text-gray-500"
                  title={`${inherited} of the ${models.length} stages here have no model of their own - they follow a shared row or the account DEFAULT.`}
                >
                  {inherited} of {models.length} inherited
                </span>
              )}
            </div>
            <ul className="space-y-2">
              {models.map(m => (
                <li key={m.stage} className="text-xs">
                  <div className="flex items-baseline justify-between gap-2">
                    <Link
                      to={agentSettingsUrl(m.stage, returnTo, { open: true })}
                      onClick={() => setOpen(false)}
                      className="font-medium text-gray-700 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline"
                      title={`Open the ${m.stage} stage in pipeline settings`}
                    >
                      {m.role || m.stage_label}
                    </Link>
                    <span className="flex items-baseline gap-1 min-w-0">
                      {m.label ? (
                        <span
                          className="font-mono text-[11px] text-indigo-600 dark:text-indigo-400 truncate"
                          title={m.label}
                        >
                          {m.label}
                        </span>
                      ) : (
                        <span className="font-mono text-[11px] text-amber-600 dark:text-amber-400">
                          not configured
                        </span>
                      )}
                      {/* The point of the whole widget: this stage has no
                          setting of its own, it's riding a shared row — the
                          chat group or the account DEFAULT — and will move when
                          that moves. Naming WHICH one is the difference between
                          a useful tag and a shrug. */}
                      {m.label && m.inherited && (
                        <span
                          className="shrink-0 rounded bg-gray-100 dark:bg-gray-700 px-1 text-[9px] uppercase tracking-wide text-gray-500 dark:text-gray-400"
                          title={
                            m.inherited_from
                              ? `No model is set for this stage on its own - it inherits "${m.inherited_from}", so it changes when that changes.`
                              : 'No model is set for this stage - it inherits the DEFAULT stage’s model, so it changes when DEFAULT changes.'
                          }
                        >
                          {m.inherited_from ? 'shared' : 'default'}
                        </span>
                      )}
                      {/* The house model, on the customer's own key. */}
                      {m.label && m.own_key && (
                        <span
                          className="shrink-0 rounded bg-emerald-50 dark:bg-emerald-900/40 px-1 text-[9px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300"
                          title="This runs on a key you registered under Settings ▸ AI ▸ Providers, at the own-key rate."
                        >
                          your key
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-1.5">
                    <span className="font-mono text-[9px] uppercase tracking-wide text-gray-300 dark:text-gray-600">
                      {m.stage}
                    </span>
                    {CAPABILITY_BADGE[m.capability] && (
                      <span
                        className="rounded bg-gray-100 dark:bg-gray-700 px-1 text-[9px] text-gray-500 dark:text-gray-400"
                        title={m.hint}
                      >
                        needs {CAPABILITY_BADGE[m.capability]}
                      </span>
                    )}
                  </div>
                  {m.blurb && (
                    <p className="mt-0.5 text-[11px] leading-snug text-gray-400 dark:text-gray-500">
                      {m.blurb}
                    </p>
                  )}
                  {/* THE CAST. A stage with seats is really several models: each
                      persona runs on its own pinned row or on the stage's shared
                      one. Listed under the stage so "which model is the skeptic
                      on" is answered here rather than three clicks into settings. */}
                  {(m.seats?.length ?? 0) > 0 && (
                    <ul className="mt-1 ml-2 border-l border-gray-100 dark:border-gray-700 pl-2 space-y-0.5" data-testid={`seats-${m.stage}`}>
                      {(m.seats ?? []).map(s => (
                        <li key={s.archetype} className="flex items-baseline justify-between gap-2 text-[11px]">
                          {/* Each seat opens the stage's dialog with THIS seat lit,
                              so changing one persona's model is one click, not a
                              scroll through eight. */}
                          <Link
                            to={seatSettingsUrl(m.stage, s.archetype, returnTo)}
                            onClick={() => setOpen(false)}
                            className="text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline"
                            title={`Change the ${s.label} model`}
                            data-testid={`seat-link-${s.archetype}`}
                          >
                            {s.label}
                          </Link>
                          <span className="flex items-baseline gap-1 min-w-0">
                            {s.model_label ? (
                              <span className={`font-mono text-[10px] truncate ${s.pinned ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'}`} title={s.model_label}>
                                {s.model_label}
                              </span>
                            ) : (
                              <span className="font-mono text-[10px] text-amber-600 dark:text-amber-400">not configured</span>
                            )}
                            {s.pinned && (
                              <span className="shrink-0 rounded bg-indigo-50 dark:bg-indigo-900/30 px-1 text-[9px] uppercase tracking-wide text-indigo-600 dark:text-indigo-300" title="This seat has a model of its own, pinned in settings.">
                                own
                              </span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
            <Link
              to={pipelineSettingsUrl(returnTo)}
              onClick={() => setOpen(false)}
              className="mt-3 block border-t border-gray-100 dark:border-gray-700 pt-2 text-[11px] font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Change models in Settings →
            </Link>
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
